/**
 * Workspace operations — list, create, delete, update, users, copy.
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { requireData, requireOk } from '../errors.js'

type WorkspaceUpdateRequest = components['schemas']['WorkspaceUpdateRequest']

export async function listWorkspaces(arbi: ArbiClient) {
  return requireData(await arbi.fetch.GET('/v1/user/workspaces'), 'Failed to fetch workspaces')
}

/**
 * Create a workspace with a pre-encrypted workspace key.
 *
 * The caller must generate a random 32-byte key, encrypt it with the session
 * public key (SealedBox via `sealKeyForSession`), and pass it here.
 * Use `Arbi.workspaces.create()` for automatic key generation.
 */
export async function createWorkspace(
  arbi: ArbiClient,
  name: string,
  encryptedWorkspaceKey: string,
  projectExtId: string,
  description?: string | null,
  isPublic = false
) {
  return requireData(
    await arbi.fetch.POST('/v1/workspace/create_protected', {
      body: {
        name,
        description: description ?? null,
        is_public: isPublic,
        workspace_key: encryptedWorkspaceKey,
        project_ext_id: projectExtId,
      },
    }),
    'Failed to create workspace'
  )
}

export async function deleteWorkspaces(arbi: ArbiClient, workspaceIds: string[]) {
  requireOk(
    await arbi.fetch.DELETE('/v1/workspace/', {
      body: { external_ids: workspaceIds },
    }),
    'Failed to delete workspace(s)'
  )
}

export async function updateWorkspace(arbi: ArbiClient, body: WorkspaceUpdateRequest) {
  return requireData(
    await arbi.fetch.PATCH('/v1/workspace/', { body }),
    'Failed to update workspace'
  )
}

export async function listWorkspaceUsers(arbi: ArbiClient) {
  return requireData(await arbi.fetch.GET('/v1/workspace/users'), 'Failed to fetch workspace users')
}

export async function addWorkspaceUsers(
  arbi: ArbiClient,
  emails: string[],
  role: 'owner' | 'collaborator' | 'guest' = 'collaborator'
) {
  return requireData(
    await arbi.fetch.POST('/v1/workspace/users', {
      body: { emails, role },
    }),
    'Failed to add users'
  )
}

export async function removeWorkspaceUsers(arbi: ArbiClient, userIds: string[]) {
  requireOk(
    await arbi.fetch.DELETE('/v1/workspace/users', {
      body: { users: userIds.map((id) => ({ user_ext_id: id })) },
    }),
    'Failed to remove users'
  )
}

export async function setUserRole(
  arbi: ArbiClient,
  userIds: string[],
  role: 'owner' | 'collaborator' | 'guest'
) {
  return requireData(
    await arbi.fetch.PATCH('/v1/workspace/users', {
      body: { user_ext_ids: userIds, role },
    }),
    'Failed to update roles'
  )
}

export async function copyDocuments(
  arbi: ArbiClient,
  targetWorkspaceId: string,
  docIds: string[],
  targetWorkspaceKey: string
) {
  return requireData(
    await arbi.fetch.POST('/v1/workspace/copy', {
      body: {
        target_workspace_ext_id: targetWorkspaceId,
        target_workspace_key: targetWorkspaceKey,
        items: docIds,
      },
    }),
    'Failed to copy documents'
  )
}
